/**
 * Crossword engine (pure; no React). The level payload is the server's
 * public layout — block pattern, derived numbers, clues and answer lengths,
 * never the answers:
 *   { rows, cols, layout: ["..#", ...], numbers: [[r, c, n]], across: [{ number, clue, row, col, length }], down: [...] }
 * Letters are checked on the server (play session), so this module only
 * models the grid, navigation and the letters the player has typed.
 */

export const DIRECTIONS = { across: [0, 1], down: [1, 0] };
const MAX_SIZE = 21;

function entryKey(number, direction) {
  return number + "-" + direction;
}

/**
 * Validated puzzle model, or null when the payload is unusable.
 * @returns {{ rows, cols, open: boolean[][], numbers: object, entries: object[], byKey: object, cellEntries: object } | null}
 */
export function buildPuzzle(config) {
  if (!config || typeof config !== "object") return null;
  const rows = config.rows;
  const cols = config.cols;
  if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows < 1 || cols < 1 || rows > MAX_SIZE || cols > MAX_SIZE) return null;
  const layout = Array.isArray(config.layout) ? config.layout : [];
  if (layout.length !== rows) return null;
  const open = [];
  for (let r = 0; r < rows; r++) {
    const line = layout[r];
    if (typeof line !== "string" || line.length !== cols) return null;
    open.push(line.split("").map((ch) => ch !== "#"));
  }
  const numbers = {};
  (Array.isArray(config.numbers) ? config.numbers : []).forEach((n) => {
    if (Array.isArray(n) && n.length === 3 && Number.isInteger(n[0]) && Number.isInteger(n[1]) && Number.isInteger(n[2])) {
      numbers[n[0] + ":" + n[1]] = n[2];
    }
  });

  const entries = [];
  const byKey = {};
  const cellEntries = {};
  let valid = true;
  ["across", "down"].forEach((direction) => {
    const [dr, dc] = DIRECTIONS[direction];
    (Array.isArray(config[direction]) ? config[direction] : []).forEach((raw) => {
      if (!raw || !Number.isInteger(raw.number) || !Number.isInteger(raw.row) || !Number.isInteger(raw.col) || !Number.isInteger(raw.length) || raw.length < 1) {
        valid = false;
        return;
      }
      const cells = [];
      for (let k = 0; k < raw.length; k++) {
        const r = raw.row + dr * k;
        const c = raw.col + dc * k;
        if (r >= rows || c >= cols || !open[r][c]) {
          valid = false;
          return;
        }
        cells.push([r, c]);
      }
      const key = entryKey(raw.number, direction);
      const entry = { key, number: raw.number, direction, clue: typeof raw.clue === "string" ? raw.clue : "", row: raw.row, col: raw.col, length: raw.length, cells };
      entries.push(entry);
      byKey[key] = entry;
      cells.forEach(([r, c]) => {
        const id = r + ":" + c;
        cellEntries[id] = Object.assign({}, cellEntries[id], { [direction]: key });
      });
    });
  });
  if (!valid || !entries.length) return null;
  return { rows, cols, open, numbers, entries, byKey, cellEntries };
}

/** rows × cols of "" (blocks included, never filled). */
export function emptyLetters(puzzle) {
  const out = [];
  for (let r = 0; r < puzzle.rows; r++) out.push(new Array(puzzle.cols).fill(""));
  return out;
}

/** One A–Z letter from typed input (the last letter typed wins), or "". */
export function normalizeLetter(value) {
  const letters = String(value || "").toUpperCase().replace(/[^A-Z]/g, "");
  return letters ? letters.charAt(letters.length - 1) : "";
}

/** Letters as the row strings the server checks ("." empty, "#" block). */
export function proofRows(puzzle, letters) {
  const out = [];
  for (let r = 0; r < puzzle.rows; r++) {
    let line = "";
    for (let c = 0; c < puzzle.cols; c++) {
      if (!puzzle.open[r][c]) line += "#";
      else line += /^[A-Z]$/.test(letters[r][c]) ? letters[r][c] : ".";
    }
    out.push(line);
  }
  return out;
}

export function isFull(puzzle, letters) {
  for (let r = 0; r < puzzle.rows; r++) {
    for (let c = 0; c < puzzle.cols; c++) {
      if (puzzle.open[r][c] && !/^[A-Z]$/.test(letters[r][c])) return false;
    }
  }
  return true;
}

/** Entry through a cell in the preferred direction, else the other one, else null. */
export function entryAt(puzzle, r, c, direction) {
  const found = puzzle.cellEntries[r + ":" + c];
  if (!found) return null;
  const key = found[direction] || found[direction === "across" ? "down" : "across"];
  return key ? puzzle.byKey[key] : null;
}

/** Direction to use at a cell: the preferred one when an entry runs that way. */
export function directionAt(puzzle, r, c, preferred) {
  const found = puzzle.cellEntries[r + ":" + c] || {};
  if (found[preferred]) return preferred;
  return found.across ? "across" : found.down ? "down" : preferred;
}

/** The other direction when an entry runs that way through the cell. */
export function toggleDirection(puzzle, r, c, direction) {
  const other = direction === "across" ? "down" : "across";
  const found = puzzle.cellEntries[r + ":" + c] || {};
  return found[other] ? other : direction;
}

/** Arrow-key move: the next open square in that direction (blocks are skipped), or null at the edge. */
export function moveCursor(puzzle, r, c, dr, dc) {
  let nr = r + dr;
  let nc = c + dc;
  while (nr >= 0 && nc >= 0 && nr < puzzle.rows && nc < puzzle.cols) {
    if (puzzle.open[nr][nc]) return [nr, nc];
    nr += dr;
    nc += dc;
  }
  return null;
}

/** Entries in clue order: Across by number, then Down by number. */
export function orderedEntries(puzzle) {
  const list = puzzle.entries.slice();
  list.sort((a, b) => (a.direction === b.direction ? a.number - b.number : a.direction === "across" ? -1 : 1));
  return list;
}

/** Tab / Shift+Tab: the next (delta 1) or previous (-1) entry, or null past either end. */
export function nextEntry(puzzle, key, delta) {
  const list = orderedEntries(puzzle);
  const index = list.findIndex((e) => e.key === key);
  const target = index === -1 ? (delta > 0 ? 0 : list.length - 1) : index + delta;
  return target >= 0 && target < list.length ? list[target] : null;
}

/** Index of a cell inside an entry, or -1. */
export function cellIndex(entry, r, c) {
  return entry.cells.findIndex(([er, ec]) => er === r && ec === c);
}

/** Cell to put the cursor on when an entry is chosen: its first empty square, else its start. */
export function entryStart(entry, letters) {
  const empty = entry.cells.find(([r, c]) => !letters[r][c]);
  return empty || entry.cells[0];
}

/** After typing: the next empty square of the entry (else the next square), or the same square at its end. */
export function afterTyping(entry, r, c, letters) {
  const index = cellIndex(entry, r, c);
  if (index === -1) return [r, c];
  for (let k = index + 1; k < entry.cells.length; k++) {
    const [er, ec] = entry.cells[k];
    if (!letters[er][ec]) return [er, ec];
  }
  return index + 1 < entry.cells.length ? entry.cells[index + 1] : [r, c];
}

/** Backspace on an empty square: the previous square of the entry (or the same at its start). */
export function beforeCell(entry, r, c) {
  const index = cellIndex(entry, r, c);
  return index > 0 ? entry.cells[index - 1] : [r, c];
}

/** Per-entry status from the server's check response ({ entries: [{ number, direction, status }] }). */
export function statusMap(response) {
  const out = {};
  (response && Array.isArray(response.entries) ? response.entries : []).forEach((e) => {
    if (e && Number.isInteger(e.number) && DIRECTIONS[e.direction] && typeof e.status === "string") {
      out[entryKey(e.number, e.direction)] = e.status;
    }
  });
  return out;
}

/** Squares in an entry marked incorrect after a check (shown until the letter changes). */
export function wrongCells(puzzle, statuses) {
  const out = {};
  puzzle.entries.forEach((entry) => {
    if (statuses[entry.key] === "incorrect") {
      entry.cells.forEach(([r, c]) => {
        out[r + ":" + c] = true;
      });
    }
  });
  return out;
}

/** Statuses after a letter changed: every entry through that square goes back to unchecked. */
export function clearStatusesAt(puzzle, statuses, r, c) {
  const found = puzzle.cellEntries[r + ":" + c] || {};
  const next = Object.assign({}, statuses);
  if (found.across) delete next[found.across];
  if (found.down) delete next[found.down];
  return next;
}
